/* eslint-disable react/no-unescaped-entities */
import React, { useState, useEffect, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthContext } from '@/App';
import {
  BookOpen, ShieldCheck, BarChart3, Send, Handshake, Megaphone,
  Mail as MailIcon, Users, UserPlus, Star, Award, Calendar,
  Briefcase, Sparkles, AlertTriangle, ChevronRight, Menu, X,
  Info, CheckCircle2, Lock
} from 'lucide-react';

const SECTIONS = [
  { id: 'overview', label: 'Overview', icon: ShieldCheck },
  { id: 'analytics', label: 'Analytics', icon: BarChart3 },
  { id: 'communications', label: 'Communications & Email', icon: Send },
  { id: 'advertisements', label: 'Advertisements', icon: Megaphone },
  { id: 'users', label: 'Users & Members', icon: Users },
  { id: 'employees', label: 'Employees', icon: UserPlus },
  { id: 'partnerships', label: 'Partnerships', icon: Handshake },
  { id: 'contacts', label: 'Partner Contacts', icon: MailIcon },
  { id: 'testimonials', label: 'Testimonials', icon: Star },
  { id: 'events', label: 'Events', icon: Calendar },
  { id: 'businesses', label: 'Businesses', icon: Briefcase },
  { id: 'opportunities', label: 'Opportunities', icon: Sparkles },
  { id: 'security', label: 'Security & Audit', icon: Lock },
];

const Tip = ({ children }) => (
  <div className="flex gap-3 p-4 my-4 rounded-lg bg-rose-50 border border-rose-100">
    <Info className="w-5 h-5 text-rose-700 flex-shrink-0 mt-0.5" />
    <p className="text-sm text-rose-900">{children}</p>
  </div>
);

const Warn = ({ children }) => (
  <div className="flex gap-3 p-4 my-4 rounded-lg bg-amber-50 border border-amber-200">
    <AlertTriangle className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
    <p className="text-sm text-amber-900">{children}</p>
  </div>
);

const Step = ({ n, title, children }) => (
  <div className="flex gap-4 my-4">
    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-rose-800 text-white text-sm font-bold flex items-center justify-center">{n}</div>
    <div className="flex-1">
      <p className="font-semibold text-gray-900">{title}</p>
      <div className="text-gray-700 text-sm mt-1">{children}</div>
    </div>
  </div>
);

const Tab = ({ children }) => (
  <span className="inline-flex items-center px-2 py-0.5 rounded bg-rose-100 text-rose-800 text-xs font-semibold">
    {children}
  </span>
);

const Section = ({ id, icon: Icon, title, subtitle, children }) => (
  <section id={id} className="scroll-mt-24 mb-14" data-testid={`admin-guide-section-${id}`}>
    <div className="flex items-center gap-3 mb-3">
      <div className="w-10 h-10 rounded-lg bg-rose-100 text-rose-800 flex items-center justify-center">
        <Icon className="w-5 h-5" />
      </div>
      <h2 className="text-3xl font-serif text-rose-900">{title}</h2>
    </div>
    {subtitle && <p className="text-gray-600 mb-4">{subtitle}</p>}
    <div className="prose prose-rose max-w-none text-gray-800 leading-relaxed">{children}</div>
  </section>
);

const AdminGuide = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [active, setActive] = useState('overview');
  const [mobileOpen, setMobileOpen] = useState(false);

  // Admin-only page: redirect non-admins to the general user guide
  useEffect(() => {
    if (user && user.role !== 'admin') {
      navigate('/user-guide');
    }
  }, [user, navigate]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActive(e.target.id);
        });
      },
      { rootMargin: '-20% 0px -70% 0px', threshold: 0 }
    );
    SECTIONS.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setMobileOpen(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-rose-50 to-white" data-testid="admin-guide-page">
      {/* Hero */}
      <div className="bg-gradient-to-r from-rose-900 via-rose-800 to-rose-700 text-white py-14">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="flex items-center gap-3 mb-3">
            <ShieldCheck className="w-8 h-8" />
            <span className="uppercase text-xs tracking-widest text-rose-200">Admin Guide</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-serif mb-3">Running Her Enterprise Hub</h1>
          <p className="text-rose-100 max-w-2xl">
            Your reference for approving ads, managing members, sending email blasts, and everything else that keeps the platform humming.
            Use the menu on the left to jump straight to a section.
          </p>
          <Link
            to="/admin"
            className="inline-block mt-6 bg-white text-rose-800 px-5 py-2 rounded-full text-sm font-semibold hover:bg-rose-50 transition-colors"
            data-testid="admin-guide-open-panel"
          >
            Open Admin Panel →
          </Link>
        </div>
      </div>

      {/* Mobile ToC toggle */}
      <button
        onClick={() => setMobileOpen(!mobileOpen)}
        className="lg:hidden fixed bottom-6 right-6 z-40 bg-rose-800 text-white p-3 rounded-full shadow-lg"
        aria-label="Toggle contents"
        data-testid="admin-guide-mobile-toc-toggle"
      >
        {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
      </button>

      <div className="container mx-auto px-4 max-w-6xl py-10">
        <div className="flex gap-8">
          {/* Sidebar / ToC */}
          <aside
            className={`
              lg:sticky lg:top-24 lg:h-fit lg:w-64 lg:flex-shrink-0 lg:block
              ${mobileOpen ? 'fixed inset-0 z-30 bg-white p-6 pt-20 overflow-y-auto' : 'hidden'}
            `}
            data-testid="admin-guide-toc"
          >
            <p className="text-xs uppercase tracking-wider text-gray-500 mb-3">Contents</p>
            <nav className="space-y-1">
              {SECTIONS.map((s) => {
                const IconEl = s.icon;
                const isActive = active === s.id;
                return (
                  <button
                    key={s.id}
                    onClick={() => scrollTo(s.id)}
                    data-testid={`admin-guide-toc-${s.id}`}
                    className={`
                      w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm text-left transition-colors
                      ${isActive
                        ? 'bg-rose-100 text-rose-900 font-semibold'
                        : 'text-gray-700 hover:bg-rose-50 hover:text-rose-800'}
                    `}
                  >
                    <IconEl className="w-4 h-4 flex-shrink-0" />
                    <span className="flex-1">{s.label}</span>
                    {isActive && <ChevronRight className="w-3.5 h-3.5" />}
                  </button>
                );
              })}
            </nav>
          </aside>

          <main className="flex-1 min-w-0">

            <Section id="overview" icon={ShieldCheck} title="Overview"
              subtitle="What the Admin Panel gives you and how it's organised.">
              <p>
                The Admin Panel lives at{' '}
                <Link to="/admin" className="text-rose-700 underline font-semibold">
                  /admin
                </Link>{' '}
                and is visible only to users with the <strong>admin</strong> role. It's organised into tabs across the top:
              </p>
              <ul className="list-disc ml-6 space-y-1 text-sm mt-3">
                <li><Tab>Analytics</Tab>: headline stats about the community</li>
                <li><Tab>Communications</Tab>: email delivery health + newsletter blast</li>
                <li><Tab>Partnerships</Tab>: approve or reject partner applications</li>
                <li><Tab>Advertisements</Tab>: moderate ads (all / pending / active / expired)</li>
                <li><Tab>Contacts</Tab>: messages sent via Partner form</li>
                <li><Tab>Users</Tab>: view, suspend, or delete members</li>
                <li><Tab>Employees</Tab>: invite team members with restricted roles</li>
                <li><Tab>Testimonials</Tab>: approve testimonials for the homepage</li>
                <li><Tab>Member testimonials</Tab>: social proof from members</li>
                <li><Tab>Events</Tab>, <Tab>Businesses</Tab>, <Tab>Opportunities</Tab>: moderate community content</li>
              </ul>
              <Tip>
                Look at the top navigation from any page, if there are unresolved email failures in the last 24 hours,
                you'll see a small red pulsing dot on the <strong>Admin</strong> button and again on the{' '}
                <Tab>Communications</Tab> tab. That's your at-a-glance early warning system.
              </Tip>
            </Section>

            <Section id="analytics" icon={BarChart3} title="Analytics"
              subtitle="Community health at a glance.">
              <p>
                Open <Tab>Analytics</Tab> for headline metrics: total members, new signups this month, total businesses,
                events, forum posts, opportunities, and revenue (if any paid tiers or events).
              </p>
              <ul className="list-disc ml-6 space-y-1 text-sm mt-3">
                <li>Numbers update on each page load, refresh to see the latest</li>
                <li>Great for weekly reports, screenshot the panel or copy the numbers into your update</li>
                <li>Growth trends: compare "new this month" against the total to gauge momentum</li>
              </ul>
            </Section>

            <Section id="communications" icon={Send} title="Communications & Email"
              subtitle="Send newsletters, monitor deliverability.">
              <p>
                The <Tab>Communications</Tab> tab has two parts stacked vertically:
              </p>

              <h3 className="text-lg font-semibold text-rose-900 mt-6 mb-2">1) Email Delivery widget</h3>
              <p>
                Shows how many emails have been sent (green) and failed (red) in the last 24 hours and last 7 days.
                Below that: expandable lists of recent failures and recent successful sends.
              </p>
              <ul className="list-disc ml-6 space-y-1 text-sm mt-3">
                <li>Auto-refreshes every 60 seconds, no need to reload</li>
                <li>Click <strong>Refresh</strong> to force an immediate update</li>
                <li>If failed count &gt; 0, a red alert banner appears with troubleshooting hints</li>
                <li>Provider: <strong>Resend</strong> (EU-Ireland region). Sender: <code className="text-xs bg-gray-100 px-1 rounded">noreply@herenterprisehub.com</code></li>
              </ul>
              <Warn>
                If you see repeated failures, check: (1) Resend account status &amp; credit balance,
                (2) domain DNS records (DKIM, SPF, DMARC still valid), (3) any recent code deploy that changed email templates.
              </Warn>

              <h3 className="text-lg font-semibold text-rose-900 mt-6 mb-2">2) Email Blast</h3>
              <p>Send a broadcast email to all members or a filtered subset.</p>
              <Step n="1" title="Choose your audience">
                Options include All members, Premium only, Free tier, or by industry / location.
              </Step>
              <Step n="2" title="Write subject &amp; body">
                HTML supported. Keep subject &lt;60 characters for best open rates. Personalise the greeting where possible.
              </Step>
              <Step n="3" title="Preview and send">
                Preview shows exactly what recipients will see. Sending is throttled to avoid Resend rate limits.
              </Step>
              <Tip>
                Marketing emails require an <em>opt-in record</em> for GDPR compliance. Currently the platform only stores
                transactional consent, so keep blasts informational (product updates, event announcements) rather than pure marketing until we add explicit opt-in.
              </Tip>
            </Section>

            <Section id="advertisements" icon={Megaphone} title="Advertisements"
              subtitle="Approve, reject, and track paid ads submitted by members.">
              <p>The <Tab>Advertisements</Tab> tab has four sub-filters:</p>
              <ul className="list-disc ml-6 space-y-1 text-sm my-3">
                <li><strong>All</strong>: every ad ever created</li>
                <li><strong>Pending</strong>: awaiting your approval</li>
                <li><strong>Active</strong>: approved &amp; currently running</li>
                <li><strong>Expired</strong>: past their end date</li>
              </ul>

              <h3 className="text-lg font-semibold text-rose-900 mt-6 mb-2">Approving a pending ad</h3>
              <Step n="1" title="Review the content">
                Click the ad card to expand. Check image, headline, description, target URL. Reject anything off-brand,
                misleading, or illegal.
              </Step>
              <Step n="2" title="Approve or reject">
                Click <strong>Approve</strong> to publish immediately, or <strong>Reject</strong> with an optional reason
                (member gets an email).
              </Step>
              <Step n="3" title="Verify placement">
                Approved ads appear on the homepage or in the placement they paid for. Visit the site publicly to confirm.
              </Step>

              <h3 className="text-lg font-semibold text-rose-900 mt-6 mb-2">Analytics per ad</h3>
              <p>
                Each ad shows lifetime views and clicks. Members can also see this on their profile page.
                Use it to reward high-performers with feedback and coaching.
              </p>
            </Section>

            <Section id="users" icon={Users} title="Users & Members"
              subtitle="Everything about the people on your platform.">
              <p>The <Tab>Users</Tab> tab lists every registered account with role, tier, join date, and status.</p>

              <h3 className="text-lg font-semibold text-rose-900 mt-6 mb-2">Common actions</h3>
              <ul className="list-disc ml-6 space-y-2 text-sm mt-3">
                <li>
                  <strong>Suspend a member</strong>: click the three-dot menu → <em>Suspend</em>. They can no longer log in.
                  Use this for temporary bans while investigating.
                </li>
                <li>
                  <strong>Delete a member</strong>: permanent removal of the account and all their profile data.
                  Use this only when required (e.g., a GDPR Article 17 "right to be forgotten" request).
                </li>
                <li>
                  <strong>View a profile</strong>: opens their public profile in a new tab so you can see what other members see.
                </li>
              </ul>

              <Warn>
                <strong>Deletion is permanent.</strong> The member's businesses, forum posts, comments, and event RSVPs are
                affected. Where possible, prefer <em>Suspend</em> first, then confirm with the member before deleting.
              </Warn>
            </Section>

            <Section id="employees" icon={UserPlus} title="Employees"
              subtitle="Invite team members with restricted access.">
              <p>
                The <Tab>Employees</Tab> tab lets you invite people to help run the platform without giving them full admin rights.
                Employees can moderate content but not delete users or change roles.
              </p>
              <Step n="1" title="Send an invitation">
                Click <strong>+ Invite Employee</strong>. Enter their email and pick a role (currently <em>employee</em>).
              </Step>
              <Step n="2" title="They accept via email">
                They receive an invitation email with a secure token link. Clicking it lets them set their password and log in.
              </Step>
              <Step n="3" title="Manage or remove">
                From the same tab you can resend the invitation email, revoke access, or remove an employee at any time.
              </Step>
              <Tip>
                Invitation tokens expire after 7 days. If a new employee misses the window, use <em>Resend invitation</em>.
              </Tip>
            </Section>

            <Section id="partnerships" icon={Handshake} title="Partnerships"
              subtitle="Businesses and organisations that want to partner with Her Enterprise Hub.">
              <p>
                Applications submitted via the <em>Partner With Us</em> page land in <Tab>Partnerships</Tab>.
                Review the pitch, then approve, reject, or reply directly.
              </p>
              <ul className="list-disc ml-6 space-y-1 text-sm mt-3">
                <li>Approve → their business appears in the Partners section</li>
                <li>Reject → optional reason sent by email</li>
                <li>Reply → for follow-up questions before deciding</li>
              </ul>
            </Section>

            <Section id="contacts" icon={MailIcon} title="Partner Contacts"
              subtitle="Messages sent via the Partner With Us contact form.">
              <p>
                These are enquiries that came in via the public contact form. Reply from your normal email inbox
                (they arrive as regular email) or mark as handled in the panel to keep the list clean.
              </p>
            </Section>

            <Section id="testimonials" icon={Star} title="Testimonials"
              subtitle="Approve social proof for the homepage.">
              <p>Two flavours:</p>
              <ul className="list-disc ml-6 space-y-1 text-sm mt-3">
                <li>
                  <Tab>Testimonials</Tab>: submissions from the general site. Approve those you want to rotate on the homepage.
                </li>
                <li>
                  <Tab>Member testimonials</Tab>: testimonials from your own members about their HEH experience.
                  Use them in newsletters, ads, and the homepage.
                </li>
              </ul>
              <Tip>Feature a fresh testimonial each month, nothing beats a genuine quote for landing-page conversion.</Tip>
            </Section>

            <Section id="events" icon={Calendar} title="Events (Admin View)"
              subtitle="See and moderate every event on the platform.">
              <p>
                The <Tab>Events</Tab> tab lists every event. Use it to spotlight great ones on the homepage,
                remove ones that violate community rules, or edit details when a host asks you to.
              </p>
            </Section>

            <Section id="businesses" icon={Briefcase} title="Businesses (Admin View)"
              subtitle="Every business listing at a glance.">
              <p>
                From <Tab>Businesses</Tab> you can spotlight standout listings, correct typos on behalf of members,
                or remove listings that break the rules (e.g., MLM schemes, spam).
              </p>
            </Section>

            <Section id="opportunities" icon={Sparkles} title="Opportunities (Admin View)"
              subtitle="Curate the community job board.">
              <p>
                <Tab>Opportunities</Tab> lists every posted gig. Delete anything spammy, and feature quality opportunities
                by promoting them to the top of the list.
              </p>
            </Section>

            <Section id="security" icon={Lock} title="Security & Audit"
              subtitle="Where to look when something feels off.">
              <p>The platform captures a security audit log for sensitive events. You can query it via the database if needed:</p>
              <ul className="list-disc ml-6 space-y-1 text-sm mt-3">
                <li><strong>failed_login</strong>: 5 in a row triggers a 15-minute account lockout</li>
                <li><strong>role_change</strong>: any promotion / demotion</li>
                <li><strong>privilege_escalation_attempt</strong>: a non-admin tried to promote themselves (blocked)</li>
                <li><strong>super_admin_restore_attempt</strong> / <strong>super_admin_restored</strong>: audit-logged, rate-limited to 3/hour</li>
                <li><strong>password_change</strong>, <strong>admin_action</strong>: captured for accountability</li>
              </ul>

              <h3 className="text-lg font-semibold text-rose-900 mt-6 mb-2">If you lose admin access</h3>
              <p>
                Super-admins have an emergency recovery path at <code className="text-xs bg-gray-100 px-1 rounded">/restore-admin.html</code>.
                Only accounts flagged as <em>super admin</em> can be restored, protecting the platform from privilege-escalation attacks.
              </p>
              <Warn>
                Keep at least two super-admin accounts (with different emails) so a lost password on one never locks you out entirely.
              </Warn>

              <div className="grid sm:grid-cols-2 gap-2 my-4">
                {[
                  'HTTPS/TLS everywhere',
                  'Bcrypt password hashing',
                  '12-char password policy',
                  '5-fail login lockout (15 min)',
                  'XSS + CSRF protection',
                  'Rate limiting on all sensitive endpoints',
                  '2 MB file upload limit + type check',
                  'GDPR-compliant Privacy Policy live',
                ].map((f) => (
                  <div key={f} className="flex gap-2 items-start text-sm text-gray-700">
                    <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>{f}</span>
                  </div>
                ))}
              </div>

              <div className="mt-8 p-6 rounded-xl bg-gradient-to-r from-rose-900 to-rose-700 text-white">
                <p className="text-lg font-serif mb-1">You've got this.</p>
                <p className="text-rose-100 text-sm mb-4">
                  Everything the platform can do, you can now find and fix. Bookmark this page, it grows as the platform grows.
                </p>
                <Link
                  to="/admin"
                  className="inline-block bg-white text-rose-800 px-5 py-2 rounded-full font-semibold hover:bg-rose-50 transition-colors"
                  data-testid="admin-guide-cta-admin"
                >
                  Back to Admin Panel →
                </Link>
              </div>
            </Section>

          </main>
        </div>
      </div>
    </div>
  );
};

export default AdminGuide;
